import { Box, InputBase, useTheme } from "@mui/material"
import { useEffect, useState } from "react";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Shbutton from "../../theme/buttons/Shbutton";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import { useTranslation } from "react-i18next";
import { useUpdateOrderMutation } from "../../../newapi/order/ordersSlice";
import { showToastMessageUpdateRequest, showToastMessageRequestError } from "../../../utils/toasts";
import * as yup from "yup";
import ShValidatedInputDropdown from "../../theme/dropdowns/ShValidatedInputDropdown";
import { yupRequired } from "../../../utils/validation";
import { general_states } from "../../../utils/staticEnums";
import { useSelector } from "react-redux";
import { useGetSettingsQuery } from "../../../newapi/global/generalApiSlice";

const MUpdateOrderState = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [updateOrder,{isLoading}] = useUpdateOrderMutation();

    const [orderStateInfo, setOrderStateInfo] = useState({
        id: props.orderId,
        state: ""
    });

    const { data: settingsBol} = useGetSettingsQuery("bol_orders_block_snooze_from_warehouse");
    const [blockSnoozeBol, setBlockSnoozeBol] = useState(false);

    useEffect(() => {
        if (settingsBol?.length > 0 && settingsBol[0].value.toString() === "true") {
            setBlockSnoozeBol(true);
        }
    }, [settingsBol])

    const [stateValues, setStateValues] = useState([]);

    const values = [
        {id: general_states.OPEN, name: t("open")},
        {id: general_states.CANCELLED, name: t("cancelled")},
        {id: general_states.SNOOZE, name: t("snooze")},
    ];

    const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);
    const actionCustomerValue = {id: general_states.ACTION_REQUIRED_CUSTOMER, name: isFulfilment ? t("customer_action_required") : t("office_action_required")}
    const cancelledValue = {id: general_states.CANCELLED, name: t("cancelled")};
    const snoozeValue = {id: general_states.SNOOZE, name: t("snooze")};
    // Takes a flagged backorder back to a plain one. The order never left backordered, so the
    // generic "different state than the current one" filter would drop this option
    const backorderedValue = {id: general_states.BACKORDERED, name: t("backordered")};

    useEffect(() => {
        setOrderStateInfo({
            id: props.orderId,
            state: props.state
        });

        let tempValues = [...values.filter(value => value.id !== props.state)];
        if ([general_states.OPEN, general_states.REPLENISH].includes(props.state) && ["vvb", "vvb_sameday"].includes(props.deliveryMethod) && blockSnoozeBol) {
            tempValues = [...values.filter(value => value.id !== props.state && value.id === general_states.CANCELLED)];
        } else if ([general_states.FULLYPICKED, general_states.FULLYPICKED, general_states.ECHECKPAUSED, general_states.ECHECK].includes(props.state)) {
            tempValues = [...values.filter(value => value.id !== props.state && value.id !== general_states.SNOOZE)];
        } else if ([general_states.MAINTENANCE].includes(props.state)) {
            tempValues = [...values.filter(value => value.id !== props.state && value.id !== general_states.OPEN)];
        } else if ([general_states.PICKING_IN_PROGRESS].includes(props.state)) {
            tempValues = [...values.filter(value => value.id === general_states.CANCELLED)];
        } else if ([general_states.BACKORDERED].includes(props.state)) {
            // Snoozing a backorder does not change its state - it sets snoozeWhenAvailable, so
            // the order keeps receiving stock and lands in snooze once the backorder resolves
            if (props.snoozeWhenAvailable) {
                tempValues = [cancelledValue, backorderedValue];
            } else if (["vvb", "vvb_sameday"].includes(props.deliveryMethod) && blockSnoozeBol) {
                tempValues = [cancelledValue];
            } else {
                tempValues = [cancelledValue, snoozeValue];
            }
        } else if ([general_states.REPLENISH].includes(props.state)) {
            tempValues = [...values.filter(value => value.id !== props.state && [general_states.CANCELLED, general_states.SNOOZE].includes(value.id))];
        } else if ([general_states.CANCELLED].includes(props.state)) {
            tempValues = [...values.filter(value => value.id !== props.state && value.id === general_states.OPEN)];
        } else if ([general_states.ACTION_REQUIRED_WAREHOUSE].includes(props.state)) {
            tempValues = [...values.filter(value => value.id !== props.state && value.id !== general_states.SNOOZE), actionCustomerValue];
        } else if ([general_states.ACTION_REQUIRED_CUSTOMER].includes(props.state)) {
            tempValues = [...values.filter(value => value.id !== props.state && value.id !== general_states.OPEN)];
        } else if ([general_states.SHIPPED].includes(props.state)) {
            tempValues = [...values.filter(value => value.id !== props.state && value.id !== general_states.SNOOZE)];
        }

        setStateValues(tempValues);
    }, [props.orderId, props.state, props.snoozeWhenAvailable, blockSnoozeBol]);

    const [fieldErrors, setFieldErrors] = useState({});
    const [showWarningMessage, setShowWarningMessage] = useState(false);

    const rules = {
        state: yupRequired('state', t),
    };
    
    const schema = yup.object().shape(rules);
   
    const SaveOrderState = async () => {
        try {
            if (isLoading) return;
            
            setFieldErrors({});
            await schema.validate(orderStateInfo, { abortEarly: false });
            const data = await updateOrder(orderStateInfo).unwrap()
            showToastMessageUpdateRequest(t, data);

            // reset object
            setOrderStateInfo({
                id: props.orderId,
                state: ""
            });

            setShowWarningMessage(false);

            props.handleClose();
        } catch (err) {
            if (err.name === 'ValidationError') {
                // Create an errors object where keys are field names and values are error messages
                const errors = err.inner.reduce((acc, currentError) => ({
                  ...acc,
                  [currentError.path]: currentError.message
                }), {});
          
                // Set the validation errors in the state
                setFieldErrors(errors);
              } else {
                // Handle other errors, such as network errors
                showToastMessageRequestError(t("create_failed"), t, err);
              }
        }
    };
    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const handleChange = (key, value) => {
        if (key === "state" && value === "open" && [general_states.ECHECK, general_states.FULLYPICKED].includes(props.previousState)) {
            setShowWarningMessage(true);
        } else if (showWarningMessage) {
            setShowWarningMessage(false);
        }

        setOrderStateInfo(prev => ({
          ...prev,
          [key]: value
        }));
        setFieldErrors({
            ...fieldErrors,
            [key]: ''
          });
      };
      

    return(
        <SHModal
            open={props.open}
            onClose={props.handleClose}
        >
            <Box 
                sx={{
                    textAlign:"center",
                    paddingY:4,
                    paddingX:5,
                    display:"flex",
                    flexDirection:"column",
                    // alignItems:"center",
                    gap:1
                }}>
                <Subheading>{t("update_order_state")}</Subheading>
                <Text center light>{`${t("update_order_state_description")} "${t(props.state)}"`}</Text>
            </Box>

            {/* BODY */}
            <Box
                sx={{
                    background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding:3,
                    borderTopLeftRadius:16,
                    borderTopRightRadius:16,
                    display:"flex",
                    flexDirection:"column",
                    gap:2
                }}
            >
                {showWarningMessage && (
                <Box
                    sx={{
                        background:colors.orange[100],
                        py:1,
                        px:2,
                        borderRadius:2,
                        maxWidth: "400px",
                        border: `1px solid ${colors.orange[400]}`,
                        mx: "auto"
                    }}
                >
                    <Text bold>{t("update_order_state_prev_warning")}</Text>
                </Box>
                )}
                <Box 
                    sx={{
                        width:1,
                        display:"flex",
                        alignItems:"center",
                        justifyContent:"space-between",
                        gap:7
                    }}
                >

                    <Box
                        sx={{
                            width:120,
                            flexShrink:0,
                        }}
                    >
                        <Text light>{t("state")}</Text>
                    </Box>
                    <ShValidatedInputDropdown
                        height={40}
                        name="state"
                        displayName="name"
                        changeField="id"
                        options={stateValues}
                        noSelection={t("choose_option")} 
                        onChange={handleChange}
                        error={fieldErrors.state}
                    />
                </Box>
            </Box>
            <Box
                component="footer"
                sx={{
                    
                    display:"block",
                    width:1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    display:"flex",
                    alignItems:"center",
                    paddingRight:3,
                    zIndex:1058,
                    position:"relative",
                    paddingTop:2,
                    background:`${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius:24,
                    borderBottomRightRadius:24,
                    justifyContent:"space-between",
                    "@media screen and (max-width: 47.9375em)" : {
                        paddingRight:0,
                    }
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={props.handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon/>}>{t("cancel")}</Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton className={"group-btn"} color={"green"}
                    variant="contained" 
                    isLoading={isLoading}
                    onClick={() => SaveOrderState()}
                    >{t("save")}</Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>
    )
}

export default MUpdateOrderState;
